import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatInput, MatLabel, MatSuffix } from '@angular/material/input';
import { MatPaginator } from '@angular/material/paginator';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort } from '@angular/material/sort';
import { MatCell, MatCellDef, MatColumnDef, MatRow, MatRowDef, MatTable } from '@angular/material/table';

import {
  AuthorizationService,
  ClientSideDataTable,
  Column,
  Organization,
  OrganizationService,
  Store,
  StoreService,
  TitleService,
  UserPreference,
  UserService
} from '@ng-cloud/badger-core';
import { FlexDirective, LayoutAlignDirective, LayoutDirective, MediaObserver } from '@ngbracket/ngx-layout';
import * as _ from 'lodash';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'badger-portal-stores-edit',
  templateUrl: './stores-edit.component.html',
  styleUrl: './stores-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FlexDirective,
    LayoutAlignDirective,
    LayoutDirective,
    MatCard,
    MatCardContent,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatFormField,
    MatIcon,
    MatIconButton,
    MatInput,
    MatLabel,
    MatProgressSpinner,
    MatRow,
    MatRowDef,
    MatSort,
    MatSuffix,
    MatTable,
    ReactiveFormsModule
  ]
})
export class StoresEditComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  private hasAccess = false;
  dataTable: ClientSideDataTable<Store>;

  private storeSort = new Subject<void>();
  SORT_FREQUENCY = 5000;
  subscriptions: Subscription[] = [];

  homeStore: Store;
  pinnedStores: number[] = [];
  userPreferences: UserPreference;
  organizations: Organization[] = [];

  loading = signal<boolean>(true);

  constructor(
    private storeService: StoreService,
    public titleService: TitleService,
    private organizationService: OrganizationService,
    private authorizationService: AuthorizationService,
    protected userService: UserService,
    public media: MediaObserver
  ) {
    authorizationService.accessToAny(['admin']).subscribe((hasAccess) => {
      this.hasAccess = hasAccess;
      this.createStoresTable();
    });
  }

  ngOnInit() {
    const params = {};
    this.subscriptions.push(
      this.storeSort.pipe(debounceTime(this.SORT_FREQUENCY)).subscribe(() => {
        this.sortPinnedStores();
      })
    );

    this.storeService.getStores(params).subscribe((stores) => {
      const orgIds = _.uniq(_.sortBy(_.map(stores, 'organizationId')));

      this.organizationService
        .getOrganizations({ ids: orgIds })
        .subscribe((orgs) => {
          this.organizations = orgs;
          stores.forEach((store) => {
            store.organization = _.find(
              this.organizations,
              (org) => org.id === store.organizationId
            );
          });
        });

      this.userService
        .getUser(this.authorizationService.getCurrentUserId())
        .subscribe((user) => {
          this.homeStore = _.head(
            _.filter(stores, (store) => store.id === user.homeStoreId)
          );
          this.dataTable.data = _.filter(
            stores,
            (store) => !(store.id === user.homeStoreId)
          );

          this.userService
            .getOrCreateUserPreference(user)
            .subscribe((userPreferences) => {
              this.userPreferences = userPreferences;

              if (userPreferences.preferences['pinned_stores']) {
                this.pinnedStores =
                  userPreferences.preferences['pinned_stores'];
                this.sortPinnedStores();
              }
            });

          this.userService.userPreferences(user.id).subscribe((response) => {
            if (response.preferences) {
              this.userPreferences.preferences = response.preferences;
              this.pinnedStores = response.preferences['pinned_stores'];
              this.storeSort.next();
            }
          });
          this.loading.set(false);
        });
    });

    this.dataTable.sort = this.sort;
    this.dataTable.filter = new FormGroup({
      filter: new FormControl()
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  trackById(index: number, store: Store): number {
    return store.id;
  }

  pinStore(storeId: number) {
    if (this.isStorePinned(storeId)) {
      this.pinnedStores = _.remove(this.pinnedStores, (id) => id !== storeId);
    }
    else {
      this.pinnedStores.push(storeId);
    }
    if (this.userPreferences.preferences) {
      this.userPreferences.preferences['pinned_stores'] = this.pinnedStores;
      this.userService.updateUserPreference(this.userPreferences).subscribe();
    }
  }

  isStorePinned(storeId: number): boolean {
    return _.includes(this.pinnedStores, storeId);
  }

  sortPinnedStores() {
    const data = this.dataTable.data.slice();
    this.dataTable.data = data.sort((a, b) => {
      const valueA = this.isStorePinned(a.id);
      const valueB = this.isStorePinned(b.id);

      if ((valueA && valueB) || (!valueA && !valueB)) {
        return a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1;
      }
      else if (valueA && !valueB) {
        return -1;
      }
      else {
        return 1;
      }
    });
  }

  getStoreSolution(solutions) {
    let solutionsString = '';
    if (solutions != null) {
      solutionsString = solutions.join(', ');
      solutionsString = _.replace(solutionsString, 'inspect', 'InSpect');
      solutionsString = _.replace(solutionsString, 'insight', 'InSight');
      solutionsString = _.replace(
        solutionsString,
        'uv_solution',
        'UV Solution'
      );
      return solutionsString;
    }
    else {
      return solutionsString;
    }
  }

  clearSearch() {
    this.dataTable.filter.setValue({ filter: '' });
  }

  private createStoresTable() {
    const columnDefs: Column[] = [];
    columnDefs.push(
      new Column('name', {
        filterBy: (store: Store) => [
          store.name,
          store.city,
          store.state,
          store.organization?.name,
          store.clientStoreId,
          store.address,
          store.zipCode
        ]
      })
    );

    this.dataTable = new ClientSideDataTable<Store>(columnDefs);
  }
}
